import React, { useEffect, useState } from "react";
import Layout from "../layout/Layout";
import ContentWrapper from "../components/ContentWrapper";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams, useNavigate } from "react-router-dom";
import { careseekerlist } from "../Redux/Action/CareSeekerAction";
import { sendNotification } from "../Redux/Action/Notification";
import { 
  getNotificationTemplates, 
  createNotificationTemplate,
  updateNotificationTemplate,
  deleteNotificationTemplate,
  incrementTemplateUsage
} from "../Redux/Action/NotificationTemplateAction";
import { toast } from "react-toastify";
import BackButton from "../components/ui/BackButton";

export default function Notification() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [selected, setSelected] = useState([]);
  const [message, setMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('compose'); // compose, templates, history
  const [allUsers, setAllUsers] = useState([]); // Store all fetched users
  const [fetchProgress, setFetchProgress] = useState({ current: 0, total: 0 }); // Track fetch progress
  
  // Template management states
  const [templateSearch, setTemplateSearch] = useState("");
  const [templateCategory, setTemplateCategory] = useState("all");
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    title: "",
    content: "",
    category: "general"
  });
  
  useEffect(() => {
    const loadUsers = async () => {
      try {
        setLoading(true);
        setError(null);
        setFetchProgress({ current: 0, total: 0 });
        
        const searchEmail = searchParams.get('search');
        
        if (searchEmail) {
          // If there's a search parameter, only fetch matching users
          console.log("Searching for users matching:", searchEmail);
          
          const result = await dispatch(
            careseekerlist({
              filters: { 
                role: ["care seeker", "carer"],
                is_deleted: false 
              },
              search: searchEmail, // Use the search parameter
              page: 1,
              limit: 100 // Should be enough for search results
            })
          );
          
          const searchUsers = result.payload?.data || [];
          console.log(`Found ${searchUsers.length} users matching search: ${searchEmail}`);
          
          setAllUsers(searchUsers);
          setFetchProgress({ current: searchUsers.length, total: searchUsers.length });
          
        } else {
          // No search parameter, fetch all users with pagination
          let allUsers = [];
          let currentPage = 1;
          let hasMoreUsers = true;
          let totalItems = 0;
          const limit = 100; // Maximum allowed by validator
          
          console.log("Starting to fetch all users...");
          
          while (hasMoreUsers) {
            console.log(`Fetching page ${currentPage} with limit ${limit}`);
            
            const result = await dispatch(
              careseekerlist({
                filters: { 
                  role: ["care seeker", "carer"],
                  is_deleted: false 
                },
                page: currentPage,
                limit: limit
              })
            );
            
            // Extract users from the result
            const pageUsers = result.payload?.data || [];
            totalItems = result.payload?.totalItems || totalItems;
            
            console.log(`Page ${currentPage}: Got ${pageUsers.length} users, Total: ${totalItems}`);
            
            // Add users to our collection
            allUsers = [...allUsers, ...pageUsers];
            
            // Update progress
            setFetchProgress({ current: allUsers.length, total: totalItems });
            
            // Check if we have more users to fetch
            const totalFetched = currentPage * limit;
            hasMoreUsers = totalFetched < totalItems && pageUsers.length === limit;
            
            currentPage++;
            
            // Safety check to prevent infinite loops
            if (currentPage > 50) {
              console.warn("Reached maximum page limit (50), stopping fetch");
              break;
            }
          }
          
          console.log(`Finished fetching all users. Total: ${allUsers.length}`);
          
          // Update the component state with all users
          setAllUsers(allUsers);
          setFetchProgress({ current: allUsers.length, total: allUsers.length });
        }
        
      } catch (err) {
        console.error("Error loading users:", err);
        setError("Failed to load users. Please refresh the page.");
        toast.error("Failed to load users. Please refresh the page.");
      } finally {
        setLoading(false);
      }
    };
    
    loadUsers();
  }, [dispatch, searchParams]);

  const userList = useSelector((state) => state.careseeker.careseekerArr);
  const templatesState = useSelector((state) => state.NotificationTemplates);
  const { templates, loading: templatesLoading } = templatesState;
  // Use allUsers if available (from pagination), otherwise fall back to Redux state
  const users = allUsers.length > 0 ? allUsers : (Array.isArray(userList) ? userList : (userList?.data || []));
  
  // Check if we're in search mode (URL parameter exists)
  const isSearchMode = searchParams.get('search') !== null;

  // Filter users based on search term
  useEffect(() => {
    if (searchTerm) {
      const filtered = users.filter(user => 
        user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [searchTerm, users]);

  // Load templates when templates tab is active
  useEffect(() => {
    if (activeTab === 'templates') {
      dispatch(getNotificationTemplates({
        search: templateSearch,
        category: templateCategory,
        page: 1,
        limit: 50
      }));
    }
  }, [dispatch, activeTab, templateSearch, templateCategory]);

  const sendMessage = async () => {
    // In single user search mode, use the found user directly
    let userIds;
    let targetUserId = null;
    
    if (isSearchMode && users.length === 1) {
      userIds = [users[0]._id];
      targetUserId = users[0]._id;
    } else {
      if (selected.length === 0) {
        toast.error("Please select at least one user.");
        return;
      }
      userIds = selected.map(object => object.value);
      // If there's a search parameter and only one user selected, get the user ID for redirect
      if (isSearchMode && selected.length === 1) {
        targetUserId = selected[0].value;
      }
    }
    
    if (message.trim().length === 0) {
      toast.error("Please enter a notification message.");
      return;
    }

    try {
      setSending(true);
      await dispatch(
        sendNotification({
          userIds: userIds,
          content: message.trim(),
        })
      );
      
      toast.success(`Notification sent successfully to ${userIds.length} user(s)!`);
      
      // If there's a search parameter and we have a target user, redirect to their profile
      if (isSearchMode && targetUserId) {
        // Small delay to let the user see the success message
        setTimeout(() => {
          navigate(`/users/${targetUserId}`);
        }, 1500);
      } else {
        // Reset form and clear URL parameters for normal flow
        setSelected([]);
        setMessage("");
        clearUrlParams();
      }
      
    } catch (error) {
      console.error("Error sending notification:", error);
      toast.error("Failed to send notification. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const handleTemplateSelect = (template) => {
    setMessage(template.content);
    setActiveTab('compose');
    // Track template usage
    dispatch(incrementTemplateUsage(template._id));
  };

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({
      title: "",
      content: "",
      category: "general"
    });
    setShowTemplateModal(true);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      title: template.title,
      content: template.content,
      category: template.category
    });
    setShowTemplateModal(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.title.trim() || !templateForm.content.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      if (editingTemplate) {
        await dispatch(updateNotificationTemplate({
          templateId: editingTemplate._id,
          templateData: templateForm
        }));
      } else {
        await dispatch(createNotificationTemplate(templateForm));
      }
      
      setShowTemplateModal(false);
      // Reload templates
      dispatch(getNotificationTemplates({
        search: templateSearch,
        category: templateCategory,
        page: 1,
        limit: 50
      }));
    } catch (error) {
      console.error("Error saving template:", error);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (window.confirm("Are you sure you want to delete this template?")) {
      try {
        await dispatch(deleteNotificationTemplate(templateId));
        // Reload templates
        dispatch(getNotificationTemplates({
          search: templateSearch,
          category: templateCategory,
          page: 1,
          limit: 50
        }));
      } catch (error) {
        console.error("Error deleting template:", error);
      }
    }
  };

  const clearUrlParams = () => {
    // Clear URL parameters by navigating to the same route without params
    navigate('/notifications', { replace: true });
  };

  const handleQuickSelect = (type) => {
    // Clear URL parameters when using quick select
    clearUrlParams();
    
    let quickUsers = [];
    switch(type) {
      case 'all':
        quickUsers = users.map(user => ({
          label: `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || user?.email || "User",
          value: user?._id
        }));
        break;
      case 'carers':
        quickUsers = users.filter(user => user.role === 'carer').map(user => ({
          label: `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || user?.email || "Carer",
          value: user?._id
        }));
        break;
      case 'care_seekers':
        quickUsers = users.filter(user => user.role === 'care seeker').map(user => ({
          label: `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || user?.email || "Care Seeker",
          value: user?._id
        }));
        break;
      default:
        break;
    }
    setSelected(quickUsers);
  };

  const handleClearSelection = () => {
    // Clear URL parameters and selection
    clearUrlParams();
    setSelected([]);
  };

  const handleSelectionChange = (newSelection) => {
    setSelected(newSelection);
    
    // If we're in search mode and user clears all selections, clear the search
    if (isSearchMode && newSelection.length === 0) {
      clearUrlParams();
    }
  };
  
  // Handle URL parameters for pre-selecting users
  useEffect(() => {
    const searchEmail = searchParams.get('search');
    if (searchEmail && users.length > 0) {
      // Find users that match the search email
      const matchingUsers = users.filter(user => 
        user.email && user.email.toLowerCase().includes(searchEmail.toLowerCase())
      );
      
      if (matchingUsers.length > 0) {
        const preSelected = matchingUsers.map(user => ({
          label: `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || user?.email || "User",
          value: user?._id
        }));
        setSelected(preSelected);
      }
    }
  }, [searchParams, users]);

  // Check if we should show the send button (user selected in single user mode or multiple selections)
  const canSendNotification = isSearchMode && users.length === 1 ? true : selected.length > 0;

  const styles = {
    container: {
      padding: '0',
      backgroundColor: '#f8f9fa',
      minHeight: '100vh'
    },
    header: {
      padding: '20px 24px',
      backgroundColor: 'white',
      borderBottom: '1px solid #e9ecef',
      marginBottom: '0'
    },
    breadcrumb: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '16px'
    },
    title: {
      fontSize: '28px',
      fontWeight: '600',
      color: '#2c3e50',
      margin: '0',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    subtitle: {
      color: '#6c757d',
      fontSize: '14px',
      margin: '8px 0 0 0'
    },
    mainContent: {
      padding: '24px',
      maxWidth: '1200px',
      margin: '0 auto'
    },
    tabContainer: {
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      overflow: 'hidden',
      marginBottom: '24px',
      border: '1px solid #e5e7eb'
    },
    tabHeader: {
      display: 'flex',
      borderBottom: '1px solid #e5e7eb',
      backgroundColor: '#f9fafb'
    },
    tab: {
      padding: '16px 24px',
      border: 'none',
      backgroundColor: 'transparent',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      color: '#6b7280',
      borderBottom: '3px solid transparent',
      transition: 'all 0.2s ease'
    },
    activeTab: {
      color: '#059669',
      borderBottomColor: '#059669',
      backgroundColor: 'white'
    },
    tabContent: {
      padding: '24px'
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      padding: '24px',
      marginBottom: '24px',
      border: '1px solid #e5e7eb'
    },
    alertInfo: {
      padding: '16px',
      backgroundColor: '#dbeafe',
      border: '1px solid #93c5fd',
      borderRadius: '8px',
      marginBottom: '24px',
      color: '#1e40af',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    alertWarning: {
      padding: '16px',
      backgroundColor: '#fef3c7',
      border: '1px solid #fcd34d',
      borderRadius: '8px',
      marginBottom: '24px',
      color: '#92400e',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    alertError: {
      padding: '16px',
      backgroundColor: '#fee2e2',
      border: '1px solid #fca5a5',
      borderRadius: '8px',
      marginBottom: '24px',
      color: '#dc2626',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    }
  };

  return (
    <Layout>
      <ContentWrapper>
        <div style={styles.container}>
          {/* Header Section */}
          <div style={styles.header}>
            <div style={styles.breadcrumb}>
              <BackButton />
              <nav style={{ fontSize: '14px', color: '#6c757d' }}>
                <a href="/dashboard" style={{ color: '#007bff', textDecoration: 'none' }}>Dashboard</a>
                {' > '}
                <span style={{ color: '#6c757d' }}>Send Notifications</span>
              </nav>
            </div>
            <h1 style={styles.title}>
              <i className="fas fa-bell" style={{ color: '#059669' }}></i>
              Send Notifications
            </h1>
            <p style={styles.subtitle}>
              Send push notifications to users across the platform
            </p>
          </div>

          <div style={styles.mainContent}>
            {/* Loading State */}
            {loading && (
              <div style={styles.card}>
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div style={{ 
                    display: 'inline-block', 
                    width: '40px', 
                    height: '40px', 
                    border: '4px solid #f3f3f3', 
                    borderTop: '4px solid #059669', 
                    borderRadius: '50%', 
                    animation: 'spin 1s linear infinite',
                    marginBottom: '16px'
                  }}></div>
                  <p style={{ color: '#6b7280', margin: '0 0 8px 0' }}>
                    {isSearchMode ? 'Searching users...' : 'Loading users...'}
                  </p>
                  {fetchProgress.total > 0 && !isSearchMode && (
                    <div style={{ marginTop: '12px' }}>
                      <div style={{
                        width: '100%',
                        backgroundColor: '#e5e7eb',
                        borderRadius: '8px',
                        height: '8px',
                        marginBottom: '8px'
                      }}>
                        <div style={{
                          width: `${(fetchProgress.current / fetchProgress.total) * 100}%`,
                          backgroundColor: '#059669',
                          height: '100%',
                          borderRadius: '8px',
                          transition: 'width 0.3s ease'
                        }}></div>
                      </div>
                      <p style={{ color: '#6b7280', fontSize: '14px', margin: '0' }}>
                        Loaded {fetchProgress.current} of {fetchProgress.total} users
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div style={styles.alertError}>
                <i className="fas fa-exclamation-triangle"></i>
                <div style={{ flex: 1 }}>
                  <strong>Error:</strong> {error}
                </div>
                <button 
                  onClick={() => window.location.reload()}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  <i className="fas fa-redo" style={{ marginRight: '6px' }}></i>
                  Retry
                </button>
              </div>
            )}

            {/* Search Mode Indicator */}
            {!loading && !error && isSearchMode && (
              <div style={{
                ...styles.card,
                padding: '16px',
                marginBottom: '24px',
                backgroundColor: '#f0fdf4',
                border: '2px solid #059669'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <i className="fas fa-search" style={{ color: '#059669', fontSize: '18px' }}></i>
                  <div>
                    <h4 style={{ margin: '0 0 4px 0', color: '#374151', fontSize: '16px', fontWeight: '600' }}>
                      Search Mode Active
                    </h4>
                    <p style={{ margin: '0', color: '#6b7280', fontSize: '14px' }}>
                      Showing users matching: <strong>"{decodeURIComponent(searchParams.get('search'))}"</strong>
                      {users.length === 0 && ' (No matches found)'}
                      {users.length > 0 && ` (${users.length} ${users.length === 1 ? 'match' : 'matches'} found)`}
                    </p>
                  </div>
                  <button
                    onClick={clearUrlParams}
                    style={{
                      marginLeft: 'auto',
                      padding: '8px 12px',
                      backgroundColor: '#059669',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}
                  >
                    <i className="fas fa-times" style={{ marginRight: '4px' }}></i>
                    Clear Search
                  </button>
                </div>
              </div>
            )}

            {/* URL Pre-selection Info */}
            {!loading && searchParams.get('search') && selected.length > 0 && (
              <div style={styles.alertInfo}>
                <i className="fas fa-info-circle"></i>
                <div>
                  <strong>Auto-selected:</strong> {selected.length} user(s) matching email "{searchParams.get('search')}"
                </div>
              </div>
            )}

            {/* No matches warning */}
            {!loading && searchParams.get('search') && selected.length === 0 && users.length > 0 && (
              <div style={styles.alertWarning}>
                <i className="fas fa-exclamation-triangle"></i>
                <div>
                  <strong>No matches:</strong> No users found matching email "{searchParams.get('search')}"
                </div>
              </div>
            )}

            {!loading && !error && (
              <>
                {/* Statistics Cards - Only show when not in search mode */}
                {!isSearchMode && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                    <div style={{
                      ...styles.card,
                      padding: '20px',
                      textAlign: 'center',
                      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                      color: 'white',
                      marginBottom: '0'
                    }}>
                      <i className="fas fa-users" style={{ fontSize: '24px', marginBottom: '8px' }}></i>
                      <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{users.length}</div>
                      <div style={{ fontSize: '14px', opacity: 0.9 }}>Total Users</div>
                    </div>
                    <div style={{
                      ...styles.card,
                      padding: '20px',
                      textAlign: 'center',
                      background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
                      color: 'white',
                      marginBottom: '0'
                    }}>
                      <i className="fas fa-user-check" style={{ fontSize: '24px', marginBottom: '8px' }}></i>
                      <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{selected.length}</div>
                      <div style={{ fontSize: '14px', opacity: 0.9 }}>Selected</div>
                    </div>
                    <div style={{
                      ...styles.card,
                      padding: '20px',
                      textAlign: 'center',
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      color: 'white',
                      marginBottom: '0'
                    }}>
                      <i className="fas fa-user-tie" style={{ fontSize: '24px', marginBottom: '8px' }}></i>
                      <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                        {users.filter(u => u.role === 'carer').length}
                      </div>
                      <div style={{ fontSize: '14px', opacity: 0.9 }}>Carers</div>
                    </div>
                    <div style={{
                      ...styles.card,
                      padding: '20px',
                      textAlign: 'center',
                      background: 'linear-gradient(135deg, #34d399 0%, #10b981 100%)',
                      color: 'white',
                      marginBottom: '0'
                    }}>
                      <i className="fas fa-user-friends" style={{ fontSize: '24px', marginBottom: '8px' }}></i>
                      <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                        {users.filter(u => u.role === 'care seeker').length}
                      </div>
                      <div style={{ fontSize: '14px', opacity: 0.9 }}>Care Seekers</div>
                    </div>
                  </div>
                )}

                {/* Main Tab Container */}
                <div style={styles.tabContainer}>
                  {/* Tab Headers */}
                  <div style={styles.tabHeader}>
                    <button
                      style={{
                        ...styles.tab,
                        ...(activeTab === 'compose' ? styles.activeTab : {})
                      }}
                      onClick={() => setActiveTab('compose')}
                    >
                      <i className="fas fa-edit" style={{ marginRight: '8px' }}></i>
                      Compose
                    </button>
                    <button
                      style={{
                        ...styles.tab,
                        ...(activeTab === 'templates' ? styles.activeTab : {})
                      }}
                      onClick={() => setActiveTab('templates')}
                    >
                      <i className="fas fa-file-alt" style={{ marginRight: '8px' }}></i>
                      Templates
                    </button>
                  </div>

                  {/* Tab Content */}
                  <div style={styles.tabContent}>
                    {activeTab === 'compose' && (
                      <div>
                        {/* Quick Selection Buttons - Only show when not in search mode */}
                        {!isSearchMode && (
                          <div style={{ marginBottom: '24px' }}>
                            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
                              <i className="fas fa-mouse-pointer" style={{ marginRight: '8px', color: '#059669' }}></i>
                              Quick Select
                            </h3>
                            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                              <button
                                onClick={() => handleQuickSelect('all')}
                                style={{
                                  padding: '10px 16px',
                                  backgroundColor: '#059669',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseOver={(e) => e.target.style.backgroundColor = '#047857'}
                                onMouseOut={(e) => e.target.style.backgroundColor = '#059669'}
                              >
                                <i className="fas fa-users" style={{ marginRight: '6px' }}></i>
                                All Users ({users.length})
                              </button>
                              <button
                                onClick={() => handleQuickSelect('carers')}
                                style={{
                                  padding: '10px 16px',
                                  backgroundColor: '#0d9488',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseOver={(e) => e.target.style.backgroundColor = '#0f766e'}
                                onMouseOut={(e) => e.target.style.backgroundColor = '#0d9488'}
                              >
                                <i className="fas fa-user-tie" style={{ marginRight: '6px' }}></i>
                                All Carers ({users.filter(u => u.role === 'carer').length})
                              </button>
                              <button
                                onClick={() => handleQuickSelect('care_seekers')}
                                style={{
                                  padding: '10px 16px',
                                  backgroundColor: '#10b981',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseOver={(e) => e.target.style.backgroundColor = '#059669'}
                                onMouseOut={(e) => e.target.style.backgroundColor = '#10b981'}
                              >
                                <i className="fas fa-user-friends" style={{ marginRight: '6px' }}></i>
                                All Care Seekers ({users.filter(u => u.role === 'care seeker').length})
                              </button>
                              {selected.length > 0 && (
                                <button
                                  onClick={handleClearSelection}
                                  style={{
                                    padding: '10px 16px',
                                    backgroundColor: '#dc2626',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    transition: 'all 0.2s ease'
                                  }}
                                  onMouseOver={(e) => e.target.style.backgroundColor = '#b91c1c'}
                                  onMouseOut={(e) => e.target.style.backgroundColor = '#dc2626'}
                                >
                                  <i className="fas fa-times" style={{ marginRight: '6px' }}></i>
                                  Clear Selection
                                </button>
                              )}
                            </div>
                          </div>
                        )}

                        {/* User Selection */}
                        <div style={{ marginBottom: '24px' }}>
                          <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
                            <i className="fas fa-user-check" style={{ marginRight: '8px', color: '#059669' }}></i>
                            {isSearchMode && users.length === 1 ? 'Selected Recipient' : 'Select Recipients'}
                          </h3>
                          
                          {/* Show simple user card when only one user is found in search mode */}
                          {isSearchMode && users.length === 1 ? (
                            <div style={{
                              padding: '16px',
                              backgroundColor: '#f0fdf4',
                              border: '2px solid #059669',
                              borderRadius: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                {/* Profile Picture or Avatar */}
                                <div style={{ position: 'relative' }}>
                                  {users[0]?.profile_picture ? (
                                    <img
                                      src={users[0].profile_picture}
                                      alt="Profile"
                                      style={{
                                        width: '48px',
                                        height: '48px',
                                        borderRadius: '50%',
                                        objectFit: 'cover',
                                        border: '2px solid #059669'
                                      }}
                                      onError={(e) => {
                                        // Fallback to avatar if image fails to load
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                      }}
                                    />
                                  ) : null}
                                  <div style={{
                                    width: '48px',
                                    height: '48px',
                                    borderRadius: '50%',
                                    backgroundColor: '#059669',
                                    display: users[0]?.profile_picture ? 'none' : 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontWeight: 'bold',
                                    fontSize: '18px',
                                    border: '2px solid #059669'
                                  }}>
                                    {users[0]?.first_name?.charAt(0)?.toUpperCase() || users[0]?.email?.charAt(0)?.toUpperCase() || 'U'}
                                  </div>
                                </div>
                                <div>
                                  <div style={{ fontWeight: '600', color: '#374151', fontSize: '16px' }}>
                                    {`${users[0]?.first_name || ""} ${users[0]?.last_name || ""}`.trim() || users[0]?.email || "User"}
                                  </div>
                                  <div style={{ color: '#6b7280', fontSize: '14px' }}>
                                    {users[0]?.email}
                                    {users[0]?.role && (
                                      <span style={{ 
                                        marginLeft: '8px',
                                        padding: '2px 8px',
                                        backgroundColor: '#e0f2fe',
                                        color: '#0277bd',
                                        borderRadius: '12px',
                                        fontSize: '12px',
                                        textTransform: 'capitalize'
                                      }}>
                                        {users[0].role}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                {/* Go to Profile Button */}
                                <button
                                  onClick={() => {
                                    const userId = users[0]?._id;
                                    navigate(`/users/${userId}`);
                                  }}
                                  style={{
                                    padding: '8px 12px',
                                    backgroundColor: '#059669',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    transition: 'background-color 0.2s ease'
                                  }}
                                  onMouseOver={(e) => e.target.style.backgroundColor = '#047857'}
                                  onMouseOut={(e) => e.target.style.backgroundColor = '#059669'}
                                >
                                  <i className="fas fa-user"></i>
                                  Profile
                                </button>
                                {/* Change/Clear Button */}
                                <button
                                  onClick={clearUrlParams}
                                  style={{
                                    padding: '8px 12px',
                                    backgroundColor: '#dc2626',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    transition: 'background-color 0.2s ease'
                                  }}
                                  onMouseOver={(e) => e.target.style.backgroundColor = '#b91c1c'}
                                  onMouseOut={(e) => e.target.style.backgroundColor = '#dc2626'}
                                >
                                  <i className="fas fa-times"></i>
                                  Change
                                </button>
                              </div>
                            </div>
                          ) : (
                            /* Custom User Selection with Pills */
                            <div>
                              {/* Search Input */}
                              <div style={{ position: 'relative', marginBottom: '16px' }}>
                                <input
                                  type="text"
                                  placeholder={isSearchMode ? 
                                    `Search from ${users.length} matching users...` : 
                                    "Search users to add..."}
                                  value={searchTerm}
                                  onChange={(e) => setSearchTerm(e.target.value)}
                                  style={{
                                    width: '100%',
                                    padding: '12px 16px 12px 44px',
                                    border: '2px solid #d1d5db',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    outline: 'none',
                                    transition: 'border-color 0.2s ease',
                                    boxSizing: 'border-box'
                                  }}
                                  onFocus={(e) => e.target.style.borderColor = '#059669'}
                                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                                />
                                <i className="fas fa-search" style={{
                                  position: 'absolute',
                                  left: '16px',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  color: '#6b7280',
                                  fontSize: '14px'
                                }}></i>
                              </div>

                              {/* Available Users List - Appears directly below search */}
                              {searchTerm && (
                                <div style={{
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '8px',
                                  backgroundColor: 'white',
                                  maxHeight: '300px',
                                  overflowY: 'auto',
                                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                  marginBottom: '16px'
                                }}>
                                  {filteredUsers.length === 0 ? (
                                    <div style={{
                                      padding: '16px',
                                      textAlign: 'center',
                                      color: '#6b7280',
                                      fontSize: '14px'
                                    }}>
                                      <i className="fas fa-search" style={{ marginRight: '8px' }}></i>
                                      No users found matching "{searchTerm}"
                                    </div>
                                  ) : (
                                    filteredUsers.map((user, i) => {
                                      const isSelected = selected.some(s => s.value === user._id);
                                      const userName = `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || user?.email || `User ${i + 1}`;
                                      
                                      return (
                                        <div
                                          key={user._id || i}
                                          onClick={() => {
                                            if (isSelected) {
                                              // Remove from selection (don't clear search - user might want to continue selecting)
                                              const newSelection = selected.filter(s => s.value !== user._id);
                                              handleSelectionChange(newSelection);
                                            } else {
                                              // Add to selection
                                              const newSelection = [...selected, {
                                                label: userName,
                                                value: user._id
                                              }];
                                              handleSelectionChange(newSelection);
                                              // Clear search to close dropdown and allow fresh search
                                              setSearchTerm("");
                                            }
                                          }}
                                          style={{
                                            padding: '12px 16px',
                                            borderBottom: i < filteredUsers.length - 1 ? '1px solid #f3f4f6' : 'none',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            backgroundColor: isSelected ? '#f0fdf4' : 'white',
                                            transition: 'background-color 0.2s ease'
                                          }}
                                          onMouseOver={(e) => {
                                            if (!isSelected) {
                                              e.currentTarget.style.backgroundColor = '#f9fafb';
                                            }
                                          }}
                                          onMouseOut={(e) => {
                                            e.currentTarget.style.backgroundColor = isSelected ? '#f0fdf4' : 'white';
                                          }}
                                        >
                                          {/* Checkbox */}
                                          <div style={{
                                            width: '18px',
                                            height: '18px',
                                            border: `2px solid ${isSelected ? '#059669' : '#d1d5db'}`,
                                            borderRadius: '4px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: isSelected ? '#059669' : 'white',
                                            transition: 'all 0.2s ease',
                                            flexShrink: 0
                                          }}>
                                            {isSelected && (
                                              <i className="fas fa-check" style={{ 
                                                color: 'white', 
                                                fontSize: '10px' 
                                              }}></i>
                                            )}
                                          </div>

                                          {/* User Avatar */}
                                          <div style={{
                                            width: '32px',
                                            height: '32px',
                                            borderRadius: '50%',
                                            backgroundColor: '#059669',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'white',
                                            fontWeight: 'bold',
                                            fontSize: '14px',
                                            flexShrink: 0
                                          }}>
                                            {user?.first_name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                                          </div>

                                          {/* User Info */}
                                          <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{
                                              fontWeight: '500',
                                              color: '#374151',
                                              fontSize: '14px',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap'
                                            }}>
                                              {userName}
                                            </div>
                                            <div style={{
                                              color: '#6b7280',
                                              fontSize: '12px',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap'
                                            }}>
                                              {user?.email}
                                              {user?.role && (
                                                <span style={{
                                                  marginLeft: '8px',
                                                  padding: '2px 6px',
                                                  backgroundColor: '#e0f2fe',
                                                  color: '#0277bd',
                                                  borderRadius: '10px',
                                                  fontSize: '10px',
                                                  textTransform: 'capitalize'
                                                }}>
                                                  {user.role}
                                                </span>
                                              )}
                                            </div>
                                          </div>

                                          {/* Selection Status */}
                                          {isSelected && (
                                            <div style={{
                                              color: '#059669',
                                              fontSize: '12px',
                                              fontWeight: '500'
                                            }}>
                                              Selected
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })
                                  )}
                                </div>
                              )}

                              {/* Selected Users Pills */}
                              {selected.length > 0 && (
                                <div style={{ marginBottom: '16px' }}>
                                  <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '8px', 
                                    marginBottom: '8px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    color: '#374151'
                                  }}>
                                    <i className="fas fa-users" style={{ color: '#059669' }}></i>
                                    Selected Recipients ({selected.length})
                                    <button
                                      onClick={handleClearSelection}
                                      style={{
                                        marginLeft: 'auto',
                                        padding: '4px 8px',
                                        backgroundColor: '#f3f4f6',
                                        color: '#6b7280',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                      }}
                                    >
                                      <i className="fas fa-times"></i>
                                      Clear All
                                    </button>
                                  </div>
                                  <div style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '8px',
                                    padding: '12px',
                                    backgroundColor: '#f9fafb',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '8px',
                                    maxHeight: '200px',
                                    overflowY: 'auto'
                                  }}>
                                    {selected.map((selectedUser) => {
                                      const user = users.find(u => u._id === selectedUser.value);
                                      return (
                                        <div
                                          key={selectedUser.value}
                                          style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            padding: '6px 12px',
                                            backgroundColor: '#059669',
                                            color: 'white',
                                            borderRadius: '20px',
                                            fontSize: '13px',
                                            fontWeight: '500',
                                            maxWidth: '250px'
                                          }}
                                        >
                                          {/* User Avatar */}
                                          <div style={{
                                            width: '20px',
                                            height: '20px',
                                            borderRadius: '50%',
                                            backgroundColor: 'rgba(255,255,255,0.2)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '10px',
                                            fontWeight: 'bold',
                                            flexShrink: 0
                                          }}>
                                            {user?.first_name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                                          </div>
                                          
                                          {/* User Name/Email */}
                                          <span style={{ 
                                            overflow: 'hidden', 
                                            textOverflow: 'ellipsis', 
                                            whiteSpace: 'nowrap',
                                            flex: 1
                                          }}>
                                            {selectedUser.label}
                                          </span>
                                          
                                          {/* Remove Button */}
                                          <button
                                            onClick={() => {
                                              const newSelection = selected.filter(s => s.value !== selectedUser.value);
                                              handleSelectionChange(newSelection);
                                            }}
                                            style={{
                                              background: 'none',
                                              border: 'none',
                                              color: 'white',
                                              cursor: 'pointer',
                                              padding: '2px',
                                              borderRadius: '50%',
                                              width: '16px',
                                              height: '16px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              fontSize: '10px',
                                              flexShrink: 0,
                                              opacity: 0.8,
                                              transition: 'opacity 0.2s ease'
                                            }}
                                            onMouseOver={(e) => e.target.style.opacity = '1'}
                                            onMouseOut={(e) => e.target.style.opacity = '0.8'}
                                          >
                                            <i className="fas fa-times"></i>
                                          </button>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {/* Helper Text */}
                              {!searchTerm && (
                                <div style={{
                                  padding: '12px',
                                  backgroundColor: '#f9fafb',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '8px',
                                  fontSize: '14px',
                                  color: '#6b7280',
                                  textAlign: 'center'
                                }}>
                                  <i className="fas fa-info-circle" style={{ marginRight: '8px' }}></i>
                                  Start typing to search and select users, or use the quick select buttons above
                                </div>
                              )}
                            </div>
                          )}
                          
                        </div>

                        {/* Message Composition */}
                        <div style={{ marginBottom: '24px' }}>
                          <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
                            <i className="fas fa-comment-alt" style={{ marginRight: '8px', color: '#059669' }}></i>
                            Compose Message
                          </h3>
                          <div style={{ position: 'relative' }}>
                            <textarea
                              value={message}
                              onChange={(e) => {
                                if (e.target.value.length <= 400) {
                                  setMessage(e.target.value);
                                }
                              }}
                              placeholder="Enter your notification message here..."
                              style={{
                                width: '100%',
                                minHeight: '120px',
                                padding: '16px',
                                border: '2px solid #d1d5db',
                                borderRadius: '8px',
                                fontSize: '14px',
                                fontFamily: 'inherit',
                                resize: 'vertical',
                                outline: 'none',
                                transition: 'border-color 0.2s ease',
                                boxSizing: 'border-box'
                              }}
                              onFocus={(e) => e.target.style.borderColor = '#059669'}
                              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                            />
                            <div style={{
                              position: 'absolute',
                              bottom: '12px',
                              right: '16px',
                              fontSize: '12px',
                              color: message.length > 350 ? '#dc2626' : '#6b7280',
                              backgroundColor: 'white',
                              padding: '2px 6px',
                              borderRadius: '4px'
                            }}>
                              {message.length}/400
                            </div>
                          </div>
                        </div>

                        {/* Live Preview - Shows immediately after message composition */}
                        {message.trim() && canSendNotification && (
                          <div style={{
                            marginTop: '20px',
                            marginBottom: '24px',
                            padding: '20px',
                            backgroundColor: '#f0fdf4',
                            border: '2px solid #059669',
                            borderRadius: '12px',
                            boxShadow: '0 4px 12px rgba(5,150,105,0.1)'
                          }}>
                            <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#374151' }}>
                              <i className="fas fa-eye" style={{ marginRight: '8px', color: '#059669' }}></i>
                              Live Preview
                            </h4>
                            
                            {/* Mobile-style notification preview */}
                            <div style={{
                              backgroundColor: 'white',
                              padding: '16px',
                              borderRadius: '12px',
                              border: '1px solid #d1d5db',
                              marginBottom: '16px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                              position: 'relative'
                            }}>
                              {/* Notification header */}
                              <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                marginBottom: '12px',
                                paddingBottom: '8px',
                                borderBottom: '1px solid #f3f4f6'
                              }}>
                                <div style={{
                                  width: '24px',
                                  height: '24px',
                                  backgroundColor: '#059669',
                                  borderRadius: '6px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  marginRight: '12px'
                                }}>
                                  <i className="fas fa-bell" style={{ color: 'white', fontSize: '12px' }}></i>
                                </div>
                                <div>
                                  <div style={{ fontWeight: '600', color: '#374151', fontSize: '14px' }}>
                                    B4T Notification
                                  </div>
                                  <div style={{ color: '#6b7280', fontSize: '12px' }}>
                                    now
                                  </div>
                                </div>
                              </div>
                              
                              {/* Notification content */}
                              <p style={{ 
                                margin: '0', 
                                color: '#4b5563', 
                                lineHeight: '1.6',
                                fontSize: '14px'
                              }}>
                                {message}
                              </p>
                            </div>
                            
                            {/* Recipients info */}
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '12px 16px',
                              backgroundColor: 'rgba(5,150,105,0.1)',
                              borderRadius: '8px',
                              border: '1px solid rgba(5,150,105,0.2)'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <i className="fas fa-users" style={{ color: '#059669', fontSize: '14px' }}></i>
                                <span style={{ color: '#065f46', fontSize: '14px', fontWeight: '500' }}>
                                  Will be sent to {isSearchMode && users.length === 1 ? '1' : selected.length} user(s)
                                </span>
                              </div>
                              
                              {/* Show first few recipient names */}
                              {selected.length > 0 && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  {selected.slice(0, 3).map((selectedUser, index) => {
                                    const user = users.find(u => u._id === selectedUser.value);
                                    return (
                                      <div
                                        key={selectedUser.value}
                                        style={{
                                          width: '24px',
                                          height: '24px',
                                          borderRadius: '50%',
                                          backgroundColor: '#059669',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          color: 'white',
                                          fontSize: '10px',
                                          fontWeight: 'bold',
                                          border: '2px solid white',
                                          marginLeft: index > 0 ? '-8px' : '0'
                                        }}
                                        title={selectedUser.label}
                                      >
                                        {user?.first_name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                                      </div>
                                    );
                                  })}
                                  {selected.length > 3 && (
                                    <div style={{
                                      width: '24px',
                                      height: '24px',
                                      borderRadius: '50%',
                                      backgroundColor: '#6b7280',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      color: 'white',
                                      fontSize: '9px',
                                      fontWeight: 'bold',
                                      border: '2px solid white',
                                      marginLeft: '-8px'
                                    }}>
                                      +{selected.length - 3}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Send Button */}
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                          <button
                            onClick={sendMessage}
                            disabled={sending || !canSendNotification || !message.trim()}
                            style={{
                              padding: '12px 24px',
                              backgroundColor: '#059669',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: (sending || !canSendNotification || !message.trim()) ? 'not-allowed' : 'pointer',
                              fontSize: '14px',
                              fontWeight: '600',
                              opacity: (sending || !canSendNotification || !message.trim()) ? 0.6 : 1,
                              transition: 'all 0.2s ease',
                              minWidth: '140px'
                            }}
                          >
                            {sending ? (
                              <>
                                <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                                Sending...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-paper-plane" style={{ marginRight: '8px' }}></i>
                                Send Notification
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )}

                    {activeTab === 'templates' && (
                      <div>
                        {/* Template Header with Search and Actions */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                          <div>
                            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                              <i className="fas fa-file-alt" style={{ marginRight: '8px', color: '#059669' }}></i>
                              Message Templates
                            </h3>
                            <p style={{ color: '#6b7280', margin: '0' }}>
                              Choose from pre-written templates or create your own
                            </p>
                          </div>
                          <button
                            onClick={handleCreateTemplate}
                            style={{
                              padding: '10px 16px',
                              backgroundColor: '#059669',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontWeight: '500',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                          >
                            <i className="fas fa-plus"></i>
                            New Template
                          </button>
                        </div>

                        {/* Search and Filter Controls */}
                        <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
                          <div style={{ flex: '1', minWidth: '200px' }}>
                            <input
                              type="text"
                              placeholder="Search templates..."
                              value={templateSearch}
                              onChange={(e) => setTemplateSearch(e.target.value)}
                              style={{
                                width: '100%',
                                padding: '10px 12px',
                                border: '2px solid #d1d5db',
                                borderRadius: '8px',
                                fontSize: '14px',
                                outline: 'none',
                                transition: 'border-color 0.2s ease'
                              }}
                              onFocus={(e) => e.target.style.borderColor = '#059669'}
                              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                            />
                          </div>
                          <select
                            value={templateCategory}
                            onChange={(e) => setTemplateCategory(e.target.value)}
                            style={{
                              padding: '10px 12px',
                              border: '2px solid #d1d5db',
                              borderRadius: '8px',
                              fontSize: '14px',
                              outline: 'none',
                              backgroundColor: 'white',
                              minWidth: '150px'
                            }}
                          >
                            <option value="all">All Categories</option>
                            <option value="welcome">Welcome</option>
                            <option value="booking">Booking</option>
                            <option value="profile">Profile</option>
                            <option value="verification">Verification</option>
                            <option value="payment">Payment</option>
                            <option value="system">System</option>
                            <option value="support">Support</option>
                            <option value="general">General</option>
                          </select>
                        </div>

                        {/* Templates Grid */}
                        {templatesLoading ? (
                          <div style={{ textAlign: 'center', padding: '40px' }}>
                            <div style={{ 
                              display: 'inline-block', 
                              width: '32px', 
                              height: '32px', 
                              border: '3px solid #f3f3f3', 
                              borderTop: '3px solid #059669', 
                              borderRadius: '50%', 
                              animation: 'spin 1s linear infinite'
                            }}></div>
                            <p style={{ marginTop: '12px', color: '#6b7280' }}>Loading templates...</p>
                          </div>
                        ) : templates.length === 0 ? (
                          <div style={{ textAlign: 'center', padding: '40px' }}>
                            <i className="fas fa-file-alt" style={{ fontSize: '48px', color: '#d1d5db', marginBottom: '16px' }}></i>
                            <h4 style={{ color: '#374151', marginBottom: '8px' }}>No templates found</h4>
                            <p style={{ color: '#6b7280', marginBottom: '16px' }}>
                              {templateSearch || templateCategory !== 'all' 
                                ? 'Try adjusting your search or filter criteria'
                                : 'Create your first template to get started'
                              }
                            </p>
                            <button
                              onClick={handleCreateTemplate}
                              style={{
                                padding: '10px 16px',
                                backgroundColor: '#059669',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '14px'
                              }}
                            >
                              Create Template
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px' }}>
                            {templates.map(template => (
                              <div
                                key={template._id}
                                style={{
                                  padding: '20px',
                                  border: '2px solid #e5e7eb',
                                  borderRadius: '8px',
                                  backgroundColor: 'white',
                                  transition: 'all 0.2s ease',
                                  position: 'relative'
                                }}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.borderColor = '#059669';
                                  e.currentTarget.style.transform = 'translateY(-2px)';
                                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(5,150,105,0.15)';
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.borderColor = '#e5e7eb';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.boxShadow = 'none';
                                }}
                              >
                                {/* Template Header */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                                  <div style={{ flex: 1 }}>
                                    <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>
                                      {template.title}
                                    </h4>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                      <span style={{
                                        padding: '2px 8px',
                                        backgroundColor: '#e0f2fe',
                                        color: '#0277bd',
                                        borderRadius: '12px',
                                        fontSize: '11px',
                                        textTransform: 'capitalize',
                                        fontWeight: '500'
                                      }}>
                                        {template.category}
                                      </span>
                                      {template.isSystem && (
                                        <span style={{
                                          padding: '2px 8px',
                                          backgroundColor: '#fef3c7',
                                          color: '#92400e',
                                          borderRadius: '12px',
                                          fontSize: '11px',
                                          fontWeight: '500'
                                        }}>
                                          System
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {/* Action Buttons */}
                                  <div style={{ display: 'flex', gap: '4px' }}>
                                    {!template.isSystem && (
                                      <>
                                        <button
                                          onClick={() => handleEditTemplate(template)}
                                          style={{
                                            padding: '6px',
                                            backgroundColor: '#f3f4f6',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            color: '#6b7280',
                                            fontSize: '12px'
                                          }}
                                          title="Edit template"
                                        >
                                          <i className="fas fa-edit"></i>
                                        </button>
                                        <button
                                          onClick={() => handleDeleteTemplate(template._id)}
                                          style={{
                                            padding: '6px',
                                            backgroundColor: '#fef2f2',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            color: '#dc2626',
                                            fontSize: '12px'
                                          }}
                                          title="Delete template"
                                        >
                                          <i className="fas fa-trash"></i>
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </div>

                                {/* Template Content */}
                                <p style={{ 
                                  color: '#6b7280', 
                                  fontSize: '14px', 
                                  lineHeight: '1.5', 
                                  margin: '0 0 16px 0',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 3,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden'
                                }}>
                                  {template.content}
                                </p>

                                {/* Template Footer */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                                    {template.usageCount > 0 && (
                                      <span>Used {template.usageCount} times</span>
                                    )}
                                  </div>
                                  <button
                                    onClick={() => handleTemplateSelect(template)}
                                    style={{
                                      padding: '8px 12px',
                                      backgroundColor: '#059669',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      fontSize: '12px',
                                      fontWeight: '500',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '4px'
                                    }}
                                  >
                                    <i className="fas fa-check"></i>
                                    Use Template
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* CSS Animation */}
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          .multi-select {
            font-family: inherit;
          }
          
          .multi-select .dropdown-container {
            border: 2px solid #e9ecef;
            border-radius: 8px;
            min-height: 48px;
          }
          
          .multi-select .dropdown-container:focus-within {
            border-color: #007bff;
            box-shadow: 0 0 0 0.2rem rgba(0,123,255,0.25);
          }
          
          .multi-select .dropdown-heading {
            padding: 12px 16px;
          }
          
          .multi-select .panel-content {
            border-radius: 0 0 8px 8px;
            border: none;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          }
        `}</style>

        {/* Template Modal */}
        {showTemplateModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              width: '90%',
              maxWidth: '500px',
              maxHeight: '90vh',
              overflow: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0, color: '#374151' }}>
                  {editingTemplate ? 'Edit Template' : 'Create New Template'}
                </h3>
                <button
                  onClick={() => setShowTemplateModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '20px',
                    cursor: 'pointer',
                    color: '#6b7280'
                  }}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151' }}>
                  Title *
                </label>
                <input
                  type="text"
                  value={templateForm.title}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter template title"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  maxLength={100}
                />
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                  {templateForm.title.length}/100 characters
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151' }}>
                  Category
                </label>
                <select
                  value={templateForm.category}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, category: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    backgroundColor: 'white',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="general">General</option>
                  <option value="welcome">Welcome</option>
                  <option value="booking">Booking</option>
                  <option value="profile">Profile</option>
                  <option value="verification">Verification</option>
                  <option value="payment">Payment</option>
                  <option value="system">System</option>
                  <option value="support">Support</option>
                </select>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151' }}>
                  Content *
                </label>
                <textarea
                  value={templateForm.content}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Enter notification content"
                  style={{
                    width: '100%',
                    minHeight: '120px',
                    padding: '12px',
                    border: '2px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box'
                  }}
                  maxLength={400}
                />
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                  {templateForm.content.length}/400 characters
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowTemplateModal(false)}
                  style={{
                    padding: '10px 16px',
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveTemplate}
                  disabled={!templateForm.title.trim() || !templateForm.content.trim()}
                  style={{
                    padding: '10px 16px',
                    backgroundColor: '#059669',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: (!templateForm.title.trim() || !templateForm.content.trim()) ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    opacity: (!templateForm.title.trim() || !templateForm.content.trim()) ? 0.6 : 1
                  }}
                >
                  {editingTemplate ? 'Update' : 'Create'} Template
                </button>
              </div>
            </div>
          </div>
        )}
      </ContentWrapper>
    </Layout>
  );
}
